import { useRef, useState, useCallback, useEffect } from 'react';

/**
 * Hook that turns a plain <canvas ref={canvasRef} /> into a signature pad
 * that works with mouse, touch (finger/stylus) and Pointer Events (surface pen).
 *
 * Usage:
 *   const { canvasRef, handlers, clear, isEmpty, toDataURL } = useCanvasSignature();
 *   <canvas ref={canvasRef} {...handlers} width={600} height={200}
 *           className="w-full touch-none" />
 *
 * Notes:
 * - `touch-action: none` (Tailwind: `touch-none`) is REQUIRED on the canvas
 *   element so the browser does not scroll / zoom while the user signs.
 * - We prevent default on touch events to stop iOS Safari from firing synthetic
 *   mouse events afterwards (which would draw a spurious dot).
 */
export const useCanvasSignature = ({ strokeStyle = '#09090B', lineWidth = 2 } = {}) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasStrokes, setHasStrokes] = useState(false);

  const getPos = (canvas, clientX, clientY) => {
    const rect = canvas.getBoundingClientRect();
    // Convert CSS pixels to canvas coordinates (in case the canvas is scaled by CSS)
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const start = useCallback((clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y } = getPos(canvas, clientX, clientY);
    drawingRef.current = true;
    ctx.beginPath();
    ctx.moveTo(x, y);
  }, []);

  const move = useCallback((clientX, clientY) => {
    if (!drawingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y } = getPos(canvas, clientX, clientY);
    ctx.lineTo(x, y);
    ctx.strokeStyle = strokeStyle;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    if (!hasStrokes) setHasStrokes(true);
  }, [strokeStyle, lineWidth, hasStrokes]);

  const end = useCallback(() => {
    drawingRef.current = false;
  }, []);

  // React onTouchMove is passive by default and cannot preventDefault.
  // Attach a non-passive listener directly to the DOM node.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const opts = { passive: false };
    const prevent = (e) => {
      if (e.cancelable) e.preventDefault();
    };
    canvas.addEventListener('touchstart', prevent, opts);
    canvas.addEventListener('touchmove', prevent, opts);
    return () => {
      canvas.removeEventListener('touchstart', prevent, opts);
      canvas.removeEventListener('touchmove', prevent, opts);
    };
  }, []);

  const handlers = {
    // Mouse
    onMouseDown: (e) => start(e.clientX, e.clientY),
    onMouseMove: (e) => move(e.clientX, e.clientY),
    onMouseUp: end,
    onMouseLeave: end,
    // Touch (finger / stylus on mobile browsers)
    onTouchStart: (e) => {
      const t = e.touches[0];
      if (t) start(t.clientX, t.clientY);
    },
    onTouchMove: (e) => {
      const t = e.touches[0];
      if (t) move(t.clientX, t.clientY);
    },
    onTouchEnd: end,
    onTouchCancel: end,
    // Pointer (Surface pen, Windows Ink, tablets that expose Pointer API)
    onPointerDown: (e) => {
      // Skip synthetic pointer events that duplicate touches
      if (e.pointerType === 'mouse' || e.pointerType === 'pen') {
        start(e.clientX, e.clientY);
      }
    },
    onPointerMove: (e) => {
      if (e.pointerType === 'mouse' || e.pointerType === 'pen') {
        move(e.clientX, e.clientY);
      }
    },
    onPointerUp: end,
    onPointerCancel: end,
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setHasStrokes(false);
  };

  const isEmpty = () => !hasStrokes;
  const toDataURL = (type = 'image/png') => canvasRef.current?.toDataURL(type);

  return { canvasRef, handlers, clear, isEmpty, toDataURL, hasStrokes };
};
